import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type {
  Fare,
  FareSliceOutbound,
  Flights,
} from "@b2bportal/air-shopping-api";
import {
  AlgomerchTag,
  FilteredFare,
  type ISelectedTrip,
  type TripDetails,
  mapAlgomerchTexts,
} from "@hopper-b2b/types";

import { ExpandableCard, Slot } from "@hopper-b2b/ui";
import {
  useDedupeFlightsInList,
  useDeviceTypes,
  useEnableTopFlights,
} from "@hopper-b2b/utilities";
import clsx from "clsx";
import type { IStoreState } from "../../../../../reducers/types";
import type { FareclassOptionFilter } from "../../../../search/reducer";

import { setTopFlights } from "../../../actions/actions";
import { FlightAlgomerchModal } from "../../../components/FlightAlgomerchModal";
import { FlightFindMoreResults } from "../../../components/FlightList/components/FlightFindMoreResults";
import { getFaresSelector } from "../../../reducer";
import { FlightListInfo } from "./components/FlightListInfo";
import { FlightCardType } from "./components/FlightListInfo/component";
import InfiniteFlights from "./components/InfiniteFlights";
import NoFlightsMessaging from "./components/NoFlightsMessaging";
import * as constants from "./constants";
import "./styles.scss";

export interface IFlightListProps {
  flights: Flights | null;
  flightsToRender: IFlightListData[] | [];
  tripSummariesLoading: boolean | null;
  rewardsKey: string | undefined;
  handleFareSubmit: (flight: IFlightListData, fareId: string) => void;
  fareClassFilter: FareclassOptionFilter;
  handleFlightSelect: (fareTrips: any, clickedFareRating: number) => void;
  expandedFareDetails: TripDetails | null;
  departureDate: Date | null;
  returnDate?: Date | null;
  isInChooseReturnStep: boolean;
  hasFlightsError: boolean;
  selectedTrip: ISelectedTrip;
  maxFlightPrice: number;
  hasAppliedFareClassFilter: boolean;
  hasAppliedNonFareclassFilter: boolean;
  setOpenCalendarModal: (val: boolean) => void;
  isRoundTrip: boolean;
  disablePriceFreeze?: boolean;
  onErrorReload?: () => void;
}

enum ModalTypes {
  AlgomerchModal,
  MobileFlightDetails,
}

type IOpenModal = ModalTypes | false;

const SkeletonFlights = () => {
  return (
    <ExpandableCard
      key={`enabled-skeleton-${Math.round(Math.random() * 10000)}`}
      className={clsx("flight-list-item", "flight-row", "b2b")}
      variant="outlined"
      expandedCardKey={"expandedFlight"}
      cardKey={""}
      handleCardKeyChange={() => null}
      scrollExpandedIntoView={true}
      content={{
        title: (
          <FlightListInfo
            tripId={""}
            onFareClick={() => null}
            type={FlightCardType.skeleton}
          />
        ),
        body: null,
      }}
    />
  );
};

export interface IFlightListData {
  slice: string;
  fares: Fare[] | FareSliceOutbound[];
}

export const FlightList = ({
  disablePriceFreeze,
  flightsToRender,
  tripSummariesLoading,
  flights,
  handleFareSubmit,
  fareClassFilter,
  handleFlightSelect,
  expandedFareDetails,
  departureDate,
  returnDate,
  isInChooseReturnStep,
  hasFlightsError,
  maxFlightPrice,
  isRoundTrip,
  hasAppliedFareClassFilter,
  hasAppliedNonFareclassFilter,
  setOpenCalendarModal,
  onErrorReload,
}: IFlightListProps) => {
  const { matchesMobile } = useDeviceTypes();
  const dispatch = useDispatch();
  const baseFaresToShow: FilteredFare[] = useSelector(getFaresSelector);
  const topFlightsIds = useSelector(
    (state: IStoreState) => state.flightShop.topFlightsIds
  );
  const [openModal, setOpenModal] = useState<IOpenModal>(false);
  const enableTopFlights = useEnableTopFlights();
  const [numFlightsToShow, setNumFlightsToShow] = useState(
    constants.INITIAL_RESULT_SET_SIZE
  );
  const [expandedFlight, setExpandedFlight] = useState("");
  const [selectedAlgomerchTag, setSelectedAlgomerchTag] = useState(
    AlgomerchTag.Cheapest
  );

  useEffect(() => {
    if (enableTopFlights && flightsToRender) {
      dispatch(setTopFlights(flightsToRender));
    }
  }, [enableTopFlights, flightsToRender, dispatch]);

  const dedupeFlightsInList = useDedupeFlightsInList();

  const faresToShow = useMemo(() => {
    if (dedupeFlightsInList) {
      return baseFaresToShow.reduce((dedupedArr, fare) => {
        if (
          dedupedArr?.findIndex(
            (dedupedFare) => dedupedFare.flight === fare.flight
          ) === -1
        ) {
          dedupedArr.push(fare);
        }
        return dedupedArr;
      }, []);
    }
    return baseFaresToShow;
  }, [baseFaresToShow, dedupeFlightsInList]);

  const hasFlightListItems =
    flights && flightsToRender.length && faresToShow.length;

  const setFetchMoreData = useCallback(() => {
    return setTimeout(
      () => setNumFlightsToShow(numFlightsToShow + constants.SHOW_MORE_NUM),
      500
    );
  }, [numFlightsToShow]);

  const handleClickAlgomerchTag = useCallback((tagText: string) => {
    const allTags = Object.keys(AlgomerchTag);
    const selectedTag = allTags.find((tag) =>
      tagText.includes(mapAlgomerchTexts[tag])
    );

    setSelectedAlgomerchTag(
      (selectedTag as AlgomerchTag) ?? AlgomerchTag.Cheapest
    );
  }, []);

  const handleSliceSelect = useCallback(
    (fareId: string, selectedFareRating: number, flight: IFlightListData) => {
      if (fareId === expandedFlight) {
        setExpandedFlight("");
      } else {
        const flightFares = isInChooseReturnStep
          ? (flight.fares as Fare[])
          : (flight.fares as FareSliceOutbound[]);
        const fareTrips = flightFares.map((fare: Fare | FareSliceOutbound) =>
          "example" in fare
            ? fare.example
            : { fare: fare.id, trip: fare.tripId }
        );

        setExpandedFlight(fareId);
        handleFlightSelect(fareTrips, selectedFareRating);
      }
    },
    [expandedFlight, handleFlightSelect, isInChooseReturnStep]
  );

  const handleClickAlgomerch = useCallback(
    (label: string) => {
      handleClickAlgomerchTag(label);
      setOpenModal(ModalTypes.AlgomerchModal);
    },
    [handleClickAlgomerchTag]
  );

  const sortTopFaresFirst = useCallback(() => {
    return [
      ...faresToShow
        .map((f) => {
          return {
            ...f,
            tagName: topFlightsIds.filter(
              (topFlight) => topFlight.id === f.flight.slice
            ),
          };
        })
        .filter((f) => f.tagName.length)
        .sort((a, b) => {
          const aPriority = Math.min(
            ...a.tagName.map(constants.getPriorityIndex)
          );
          const bPriority = Math.min(
            ...b.tagName.map(constants.getPriorityIndex)
          );
          return aPriority - bPriority;
        }),
      ...faresToShow.filter(
        ({ flight }) =>
          !topFlightsIds.some((topFlight) => topFlight.id === flight.slice)
      ),
    ];
  }, [faresToShow, topFlightsIds]);

  const sortTopFlightsFirst = useCallback(() => {
    return [
      ...flightsToRender
        .map((f) => {
          return {
            ...f,
            tagName: topFlightsIds.filter(
              (topFlight) => topFlight.id === f.slice
            ),
          };
        })
        .filter((f) => f.tagName.length)
        .sort((a, b) => {
          const aPriority = Math.min(
            ...a.tagName.map(constants.getPriorityIndex)
          );
          const bPriority = Math.min(
            ...b.tagName.map(constants.getPriorityIndex)
          );
          return aPriority - bPriority;
        }),
      ...flightsToRender.filter(
        (f) => !topFlightsIds.some((topFlight) => topFlight.id === f.slice)
      ),
    ];
  }, [topFlightsIds, flightsToRender]);

  return (
    <div
      className={clsx(
        "flight-list",
        { "flight-list-skeleton": tripSummariesLoading },
        { mobile: matchesMobile }
      )}
    >
      {tripSummariesLoading ? (
        <Slot id="flight-search-loader" /> || <SkeletonFlights />
      ) : hasFlightListItems ? (
        <InfiniteFlights
          departureDate={departureDate}
          returnDate={returnDate}
          disablePriceFreeze={disablePriceFreeze}
          expandedFareDetails={expandedFareDetails}
          expandedFlight={expandedFlight}
          fareClassFilter={fareClassFilter}
          flights={flights}
          hasMore={
            matchesMobile
              ? numFlightsToShow < faresToShow.length
              : numFlightsToShow < flightsToRender.length
          }
          flightsToShow={
            tripSummariesLoading
              ? []
              : enableTopFlights
              ? sortTopFlightsFirst()
              : flightsToRender.slice(0, numFlightsToShow)
          }
          onAlgomerchClick={handleClickAlgomerch}
          onFareSubmit={handleFareSubmit}
          onSliceSelect={handleSliceSelect}
          isInChooseReturnStep={isInChooseReturnStep}
          isRoundTrip={isRoundTrip}
          maxFlightPrice={maxFlightPrice}
          setFetchMoreData={setFetchMoreData}
          hasAppliedFareClassFilter={hasAppliedFareClassFilter}
          setExpandedFlight={setExpandedFlight}
          faresToShow={
            enableTopFlights
              ? sortTopFaresFirst()
              : faresToShow.slice(0, numFlightsToShow)
          }
        />
      ) : (
        <NoFlightsMessaging
          hasAppliedFareClassFilter={hasAppliedFareClassFilter}
          hasAppliedNonFareclassFilter={hasAppliedNonFareclassFilter}
          hasFlightsError={hasFlightsError}
          onReload={onErrorReload}
          setOpenCalendarModal={setOpenCalendarModal}
        />
      )}
      {!tripSummariesLoading &&
      flightsToRender.length === numFlightsToShow &&
      flightsToRender.length !== 0 &&
      (hasAppliedFareClassFilter || hasAppliedNonFareclassFilter) ? (
        <FlightFindMoreResults />
      ) : null}
      <FlightAlgomerchModal
        selectedCategory={selectedAlgomerchTag}
        setSelectedCategory={setSelectedAlgomerchTag}
        openModal={openModal === ModalTypes.AlgomerchModal}
        onClose={() => setOpenModal(false)}
      />
    </div>
  );
};
